import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn, Comparator, FilterOperator } from '@/lib/utils';
import { CommandLoading } from 'cmdk';
import { Check, ChevronsUpDown, MapPin } from 'lucide-react';
import { useState } from 'react';
import { Spinner } from '@/components/ui/spinner';
import { useQuery } from '@tanstack/react-query';
import { fetchSitesMonitoring, SitesSearchDto } from '@/app/Sites/siteApi';

interface SiteSelectorProps {
  value?: string;
  onChange: (site: SitesSearchDto) => void;
  excludeProjectId?: string;
  error?: string | null;
}

export function SiteSelector({
  value,
  onChange,
  excludeProjectId,
  error,
}: SiteSelectorProps) {
  const [queryText, setQueryText] = useState('');
  const [selectorOpen, setSelectorOpen] = useState(false);

  const { data, status } = useQuery({
    queryKey: ['sites.search', queryText],
    queryFn: () =>
      fetchSitesMonitoring({
        pageNumber: 1,
        pageSize: 20,
        filterFilterGroups: queryText
          ? [
              {
                operator: FilterOperator.Or,
                filters: [
                  {
                    field: 'name',
                    action: Comparator.ILike,
                    value: queryText,
                  },
                  {
                    field: 'companyName',
                    action: Comparator.ILike,
                    value: queryText,
                  },
                ],
              },
            ]
          : undefined,
      }),
  });

  const sites = (data?.sites || []).filter((site) => site.id !== excludeProjectId);
  const selectedSite = sites.find((site) => site.id === value);

  return (
    <Popover
      open={selectorOpen}
      onOpenChange={(change) => {
        setSelectorOpen(change);
        if (!change) setQueryText('');
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={selectorOpen}
          className={cn(
            'w-full justify-between text-white bg-[#111]',
            error && 'bg-red-950 text-red-50'
          )}
        >
          <div className="flex items-center gap-2">
            <MapPin className="h-4 w-4 text-gray-400" />
            {selectedSite ? (
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">{selectedSite.name}</span>
                <span className="text-xs text-gray-400">{selectedSite.companyName}</span>
              </div>
            ) : (
              'Select site...'
            )}
          </div>
          <ChevronsUpDown className="text-lime" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command filter={() => 1}>
          <CommandInput
            placeholder="Search sites..."
            className="h-9"
            onValueChange={setQueryText}
          />
          <CommandList>
            <CommandEmpty>{status === 'success' && 'No sites found.'}</CommandEmpty>
            <CommandGroup>
              {sites.map((site) => (
                <CommandItem
                  key={site.id}
                  value={site.id}
                  onSelect={() => {
                    onChange(site);
                    setSelectorOpen(false);
                  }}
                >
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-gray-400" />
                    <div className="flex flex-col">
                      <span className="text-sm font-medium">{site.name}</span>
                      <span className="text-xs text-gray-400">{site.companyName}</span>
                    </div>
                  </div>
                  <Check
                    className={cn('ml-auto', value === site.id ? 'opacity-100' : 'opacity-0')}
                  />
                </CommandItem>
              ))}
            </CommandGroup>
            {status === 'pending' && (
              <CommandLoading>
                <Spinner />
              </CommandLoading>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
